// src/api/sseClient.ts

import { API_BASE_URL, TOKEN_KEY } from '../constants';

/**
 * SSE Client - للاتصال بـ Server-Sent Events
 *
 * Uses the same Authorization header as the axios client so the server
 * can authenticate the SSE connection the same way as regular API calls.
 */

export interface SSEOptions {
  url: string;
  onMessage: (data: any) => void;
  onError?: (error: Error) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export class SSEClient {
  private abortController: AbortController | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private isConnecting = false;

  /**
   * بناء URL كامل
   */
  private buildUrl(endpoint: string): string {
    // إزالة / من البداية إذا موجود
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${API_BASE_URL}/${cleanEndpoint}`;
  }

  /**
   * الاتصال بـ SSE
   */
  async connect(options: SSEOptions): Promise<void> {
    const { url, onMessage, onError, onOpen, onClose } = options;

    // منع محاولات اتصال متعددة
    if (this.isConnecting) {
      return;
    }

    // إلغاء أي اتصال قديم
    this.disconnect();

    try {
      this.isConnecting = true;
      this.abortController = new AbortController();

      const fullUrl = this.buildUrl(url);

      // Read token from localStorage — same source as the axios interceptor
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        const error = new Error('Authentication token not found');
        onError?.(error);
        this.isConnecting = false;
        return;
      }

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        credentials: 'include',
        signal: this.abortController.signal,
      });

      // التحقق من الاستجابة
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      console.log('✅ SSE Connection established');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      onOpen?.();

      // قراءة Stream
      await this.readStream(response.body, onMessage, onError);

    } catch (err: any) {
      this.isConnecting = false;

      // تجاهل أخطاء الإلغاء
      if (err.name === 'AbortError') {
        console.log('🔌 SSE Connection aborted');
        onClose?.();
        return;
      }

      console.error('❌ SSE Connection error:', err);
      onError?.(err);

      // محاولة إعادة الاتصال
      this.attemptReconnect(options);
    }
  }

  /**
   * قراءة SSE Stream
   */
  private async readStream(
    body: ReadableStream<Uint8Array>,
    onMessage: (data: any) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('📡 SSE Stream ended');
          break;
        }

        // فك تشفير البيانات
        buffer += decoder.decode(value, { stream: true });

        // معالجة الرسائل (كل رسالة مفصولة بـ \n\n)
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();

            // تجاهل heartbeat أو تعليقات
            if (data === '' || data.startsWith(':')) {
              continue;
            }

            try {
              const parsed = JSON.parse(data);
              onMessage(parsed);
            } catch (parseErr) {
              console.error('Error parsing SSE message:', parseErr);
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Error reading SSE stream:', err);
        onError?.(err);
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * محاولة إعادة الاتصال
   */
  private attemptReconnect(options: SSEOptions): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `🔄 Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect(options);
    }, this.reconnectDelay);
  }

  /**
   * قطع الاتصال
   */
  disconnect(): void {
    // إلغاء محاولات إعادة الاتصال
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // إلغاء الاتصال الحالي
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.isConnecting = false;
    console.log('🔌 SSE Disconnected');
  }

  /**
   * إعادة تعيين محاولات الاتصال
   */
  resetReconnectAttempts(): void {
    this.reconnectAttempts = 0;
  }

  /**
   * التحقق من حالة الاتصال
   */
  isConnected(): boolean {
    return this.abortController !== null && !this.isConnecting;
  }
}

/**
 * Singleton Instance
 */
export const sseClient = new SSEClient();

export default sseClient;